/* Offline / Service Worker helpers for ShiaTunes */

const REGISTRY_KEY = "shiatunes_offline_tracks";

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Only register in production OR when explicitly enabled in dev
  // In dev, CRA hot-reload doesn't play well with SW caching of the bundle.
  // We still want offline media — so register with a query string to avoid hash conflicts.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then(() => {
        // ready
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("SW registration failed", err);
      });
  });
}

function readRegistry() {
  try {
    return JSON.parse(localStorage.getItem(REGISTRY_KEY) || "{}");
  } catch {
    return {};
  }
}
function writeRegistry(obj) {
  try {
    localStorage.setItem(REGISTRY_KEY, JSON.stringify(obj));
    window.dispatchEvent(new CustomEvent("offline-registry-changed"));
  } catch {
    // ignore
  }
}

export function getOfflineRegistry() {
  return readRegistry();
}

export function isTrackOffline(trackId) {
  return !!readRegistry()[trackId];
}

export function getOfflineTracks() {
  const reg = readRegistry();
  return Object.values(reg).sort((a, b) => (b.cached_at || 0) - (a.cached_at || 0));
}

function postToSW(msg) {
  return new Promise((resolve) => {
    if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
      resolve(null);
      return;
    }
    const ch = new MessageChannel();
    ch.port1.onmessage = (e) => resolve(e.data);
    navigator.serviceWorker.controller.postMessage(msg, [ch.port2]);
    // safety timeout — wait for either replied port OR generic SW reply via global handler
    setTimeout(() => resolve(null), 25000);
  });
}

// Listen for SW messages (e.g. TRACK_CACHED) on the default channel and dispatch events
if (typeof navigator !== "undefined" && navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    const d = event.data || {};
    if (d.type === "TRACK_CACHED" && d.id) {
      const reg = readRegistry();
      const existing = reg[d.id] || {};
      reg[d.id] = { ...existing, id: d.id, url: d.url, cached_at: Date.now() };
      writeRegistry(reg);
    }
    if (d.type === "TRACK_REMOVED" && d.id) {
      const reg = readRegistry();
      delete reg[d.id];
      writeRegistry(reg);
    }
  });
}

export async function cacheTrack(track) {
  if (!track || !track.media_url || !track.id) return false;
  if (!navigator.serviceWorker || !navigator.serviceWorker.controller) return false;
  // Optimistically reflect in registry as "pending"
  const reg = readRegistry();
  if (!reg[track.id]) {
    reg[track.id] = {
      id: track.id,
      url: track.media_url,
      title: track.title,
      thumbnail: track.thumbnail,
      reciter_name: track.reciter_name,
      type: track.type,
      duration: track.duration,
      category_slug: track.category_slug,
      cached_at: null, // null = in progress
    };
    writeRegistry(reg);
  }
  navigator.serviceWorker.controller.postMessage({
    type: "CACHE_TRACK",
    url: track.media_url,
    id: track.id,
  });
  return true;
}

export async function removeCachedTrack(track) {
  if (!track || !track.media_url) return false;
  if (!navigator.serviceWorker || !navigator.serviceWorker.controller) return false;
  navigator.serviceWorker.controller.postMessage({
    type: "REMOVE_TRACK",
    url: track.media_url,
    id: track.id,
  });
  // Optimistic
  const reg = readRegistry();
  delete reg[track.id];
  writeRegistry(reg);
  return true;
}

export function useOnlineStatus(setOnline) {
  if (typeof window === "undefined") return;
  const update = () => setOnline(navigator.onLine);
  update();
  window.addEventListener("online", update);
  window.addEventListener("offline", update);
  return () => {
    window.removeEventListener("online", update);
    window.removeEventListener("offline", update);
  };
}
